/**
 * Тема antd для SMS-центра (ConfigProvider в main.tsx). antd считает цвета в JS, поэтому здесь значения, а не
 * `var(--fk-*)`; каждое из них обязано совпадать с токеном design/tokens.css — это проверяет тест
 * design/contrast/contrast.test.ts («тема antd = токены»). Правило то же, что в CSS (пакет 9b, п.15): базовые
 * цвета бренда — для заливок и границ, ТЕКСТ и ссылки — тёмные «*-text» оттенки с контрастом AA 4.5:1.
 */
import type { ThemeConfig } from "antd";

/** Токен → значение. Ключи — имена CSS-переменных без `--fk-`. */
export const FK_THEME_COLORS = {
  orange: "#FF6900",
  "orange-hover": "#EE6200",
  "orange-active": "#E96000",
  "orange-text": "#B04700",
  "orange-text-hover": "#8F3A00",
  "on-orange": "#251D4A",
  info: "#0096FF",
  "info-text": "#006EBB",
  success: "#7CB342",
  "success-text": "#4A7423",
  warning: "#FB8C00",
  "warning-text": "#9C5600",
  error: "#DC211C",
  "error-text": "#B3261E",
  text: "#1A1F36",
  "text-2": "#5B6178",
  "text-muted": "#68708B",
  "field-border": "#858DA8",
  border: "#E5E8F0",
  bg: "#FAFBFD",
  "table-head": "#FAFAFA",
  "disabled-bg": "#EBEDF3",
  "disabled-text": "#7F879F",
  indigo: "#251D4A",
} as const;

const C = FK_THEME_COLORS;

// Единый оранжевый фокус светлой темы FISHKA.
const FOCUS_SHADOW = "0 0 0 3px rgba(255,105,0,.35)";

export const FK_ANTD_THEME: ThemeConfig = {
  token: {
    colorPrimary: C.orange,
    // Основная кнопка: подпись индиго читается на всех трёх заливках (4.5:1 и выше); расчётные цвета antd — 3.8:1 при нажатии.
    colorPrimaryHover: C["orange-hover"],
    colorPrimaryActive: C["orange-active"],
    // Ссылки и «текстовые» цвета — затемнённые оттенки (бренд-оранжевый как текст даёт 2.9:1).
    colorLink: C["orange-text"],
    colorLinkHover: C["orange-text-hover"],
    colorLinkActive: C["orange-text-hover"],
    colorInfo: C.info,
    colorInfoText: C["info-text"],
    colorSuccess: C.success,
    colorSuccessText: C["success-text"],
    colorWarning: C.warning,
    colorWarningText: C["warning-text"],
    colorError: C.error,
    colorErrorText: C["error-text"],
    borderRadius: 6,
    borderRadiusLG: 8,
    fontFamily:
      "'FK Emoji', 'Manrope', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Apple Color Emoji', 'Noto Color Emoji', sans-serif",
    fontSize: 15,
    colorText: C.text,
    colorTextSecondary: C["text-2"],
    // Подсказки, плейсхолдеры, неактивные подписи: antd по умолчанию красит их в rgba(0,0,0,.25–.45) — 1.6–3.4:1.
    colorTextTertiary: C["text-muted"],
    colorTextDescription: C["text-muted"],
    colorTextPlaceholder: C["text-muted"],
    colorTextQuaternary: C["disabled-text"],
    colorTextDisabled: C["disabled-text"],
    colorBgContainerDisabled: C["disabled-bg"],
    // Граница поля и контурной кнопки — 3:1 (WCAG 1.4.11).
    colorBorder: C["field-border"],
    colorBorderSecondary: C.border,
    colorBgLayout: C.bg,
  },
  components: {
    // Подпись на оранжевой заливке — индиго (5.4:1), белая даёт 2.9:1.
    Button: { primaryColor: C["on-orange"], colorTextDisabled: C["disabled-text"] },
    Input: {
      activeBorderColor: C.orange,
      hoverBorderColor: C.orange,
      activeShadow: FOCUS_SHADOW,
    },
    InputNumber: {
      activeBorderColor: C.orange,
      hoverBorderColor: C.orange,
      activeShadow: FOCUS_SHADOW,
    },
    Select: {
      activeBorderColor: C.orange,
      hoverBorderColor: C.orange,
      activeOutlineColor: "rgba(255,105,0,.25)",
    },
    DatePicker: {
      activeBorderColor: C.orange,
      hoverBorderColor: C.orange,
      activeShadow: FOCUS_SHADOW,
    },
    Table: {
      headerBg: C["table-head"],
      headerColor: C["text-2"],
      rowHoverBg: "rgba(0,0,0,.025)",
      cellFontSize: 13,
    },
    Modal: { borderRadiusLG: 8, titleFontSize: 18 },
    // antd 6 кладёт текст тоста в .ant-message-notice-title, а его красит colorTextHeading — без него текст тёмный на тёмном.
    Message: {
      contentBg: C.indigo,
      colorText: "#fff",
      colorTextHeading: "#fff",
      colorIcon: "#fff",
      colorSuccess: "#fff",
      colorError: "#F8B67B",
      colorInfo: "#fff",
      borderRadiusLG: 8,
    },
  },
};
